export type HoursScheduleRow = {
  /** Day / range label; empty on continuation rows for extra shifts. */
  day: string;
  /** Shift time range or closed status. */
  time: string;
};

const TIME_RANGE_RE = /\d{1,2}[:.]\d{2}\s*[–\-—~]\s*\d{1,2}[:.]\d{2}/g;
const CLOSED_TAIL_RE =
  /^(.*?)(?:\s+)(closed|geschlossen|ruhetag|nghỉ|đóng\s*cửa)\s*$/iu;
const CLOSED_ONLY_RE = /^(?:closed|geschlossen|ruhetag|nghỉ|đóng\s*cửa)$/iu;

/** Splits free-text opening hours into raw lines (newlines + · • | ;). */
export function splitHoursLines(raw: string | null | undefined): string[] {
  if (!raw?.trim()) return [];

  return raw
    .split(/\r?\n|(?:\s*[·•|;]\s*)/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function normalizeTimeRange(raw: string): string {
  return raw.replace(/\./g, ':').replace(/\s*[–\-—~]\s*/u, ' - ');
}

function stripTrailingSeparators(value: string): string {
  return value.replace(/[\s·•|,;:\-–—]+$/u, '').trim();
}

function parseHoursLine(line: string): { day: string; times: string[] } | null {
  const trimmed = line.trim();
  if (!trimmed) return null;

  const timeMatches = [...trimmed.matchAll(TIME_RANGE_RE)];
  if (timeMatches.length > 0) {
    const firstIndex = timeMatches[0]!.index ?? 0;
    const day = stripTrailingSeparators(trimmed.slice(0, firstIndex));
    const times = timeMatches.map((match) => normalizeTimeRange(match[0]!));
    return { day, times };
  }

  const closedTail = trimmed.match(CLOSED_TAIL_RE);
  if (closedTail) {
    const day = stripTrailingSeparators(closedTail[1] ?? '');
    const status = (closedTail[2] ?? '').trim();
    return { day, times: [status] };
  }

  if (CLOSED_ONLY_RE.test(trimmed)) {
    return { day: '', times: [trimmed] };
  }

  return { day: trimmed, times: [''] };
}

/**
 * Parses free-text hours into day/time rows for a 2-column schedule.
 * Extra shifts for the same day render with an empty day cell.
 */
export function parseHoursSchedule(raw: string | null | undefined): HoursScheduleRow[] {
  const lines = splitHoursLines(raw);
  const groups: { day: string; times: string[] }[] = [];

  for (const line of lines) {
    const parsed = parseHoursLine(line);
    if (!parsed) continue;

    const isContinuation = !parsed.day && parsed.times.length > 0 && groups.length > 0;
    if (isContinuation) {
      groups[groups.length - 1]!.times.push(...parsed.times);
      continue;
    }

    groups.push({
      day: parsed.day,
      times: parsed.times.length > 0 ? parsed.times : [''],
    });
  }

  return groups.flatMap((group) =>
    group.times.map((time, index) => ({
      day: index === 0 ? group.day : '',
      time,
    })),
  );
}
